/**
 * Thin drop-target strip rendered above every step (plus one extra after the
 * last). Highlights when a reorder drag passes over it; on drop it moves the
 * dragged step to that index. Inert when no reorder drag is in progress.
 *
 * `parentLoopId` says which container this strip belongs to: `null` for the
 * pipeline root, a loop's id when rendered inside that loop's body. The drop
 * handler routes through `moveTransformStep(id, { parentLoopId }, index)`,
 * which also rejects "drag a loop into its own subtree" cases.
 */
import React, { useState } from "react"
import {
  QC_STEP_REORDER_TYPE,
  useQueryBuilderStore,
} from "stores/queryBuilderStore"

interface DropStripProps {
  index: number
  parentLoopId: string | null
}

const DropStrip: React.FC<DropStripProps> = ({ index, parentLoopId }) => {
  const moveStep = useQueryBuilderStore((s) => s.moveTransformStep)
  const [active, setActive] = useState(false)

  const accepts = (e: React.DragEvent<HTMLDivElement>): boolean =>
    e.dataTransfer.types.includes(QC_STEP_REORDER_TYPE)

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (!accepts(e)) return
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
  }
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    if (accepts(e)) setActive(true)
  }
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (e.currentTarget === e.target) setActive(false)
  }
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    setActive(false)
    const id = e.dataTransfer.getData(QC_STEP_REORDER_TYPE)
    if (id) {
      e.preventDefault()
      moveStep(id, { parentLoopId }, index)
    }
  }

  return (
    <div
      className="qc-step-row__dropstrip"
      data-active={active ? "true" : "false"}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    />
  )
}

export default DropStrip
